import type { CartItem } from '~/types/item'
import type { CampaignCode } from '~/types/cart'
import type { DeliveryMethod } from '~/types/checkout'

/**
 * Shipping benefit (toimitusetu), from tkapi-campaign-getShippingBenefitV2: when the products sum after discounts
 * reaches the threshold, the rule's delivery methods are free. Same rules as functions (helpers/shippingBenefit),
 * which validates the payment: a difference only makes the checkout ask the customer to choose again.
 */
export interface ShippingBenefit {
  id: number
  name: string
  threshold: number
  deliveryMethodIds: number[]
  /** Typesense filter_by of the products that count towards the threshold; null = all */
  productFilter: string | null
  requiresLogin: boolean
  /** false: business customers pay shipping normally. Missing (older functions) = true */
  appliesToBusinessCustomers?: boolean
  activeFrom: string
  activeTo: string
}

const round2 = (value: number) => Math.round(value * 100) / 100

/**
 * Products sum (incl. VAT) after discounts that counts towards the threshold. A percent campaign discount is
 * subtracted per discounted product, a fixed (€) one in full. countsTowardsThreshold leaves out products outside
 * the rule's product filter (spare parts always count).
 */
export const shippingBenefitBase = (
  items: CartItem[],
  campaign: CampaignCode | null,
  countsTowardsThreshold: (item: CartItem) => boolean = () => true
): number => {
  const counted = items.filter(countsTowardsThreshold)
  const products = counted.reduce((sum, item) => sum + item.price.finalPrice.inclTax * item.amount, 0)
  let discount = 0
  if (campaign?.appliesTo === 'price' || campaign?.appliesTo === undefined) {
    if (campaign?.method === 'fixed') {
      discount = campaign.discountSum || 0
    }
    else if (campaign?.discountedCartItems) {
      discount = counted.reduce((sum, item) => sum + (campaign.discountedCartItems?.find(d => d.name === item.variationName)?.totalRowDiscount || 0), 0)
    }
  }
  return round2(Math.max(products - discount, 0))
}

/** Reaching the threshold exactly is enough; business customers per rule (marketing 19.10.2026) */
export const isShippingBenefitReached = (benefit: ShippingBenefit | null, base: number, options: { isBusinessCustomer: boolean, isLoggedIn: boolean }): boolean =>
  !!benefit
  && (benefit.appliesToBusinessCustomers !== false || !options.isBusinessCustomer)
  && (!benefit.requiresLogin || options.isLoggedIn)
  && base >= benefit.threshold

/** Delivery methods with the benefit applied: the free ones cost 0 € and keep their normal price in originalPrice */
export const applyShippingBenefit = <T extends DeliveryMethod>(methods: T[], freeDeliveryMethodIds: number[]): T[] =>
  freeDeliveryMethodIds.length === 0
    ? methods
    : methods.map(method => freeDeliveryMethodIds.includes(method.id)
        ? { ...method, price: 0, priceBreakdown: { shipping: 0, officeFee: 0 }, originalPrice: method.price, shippingBenefit: true }
        : method)
